import React from 'react';
import { PieChart, Button, Select } from '@privemind/components';
import { formatCurrency, formatPnL, formatPercentage } from '@privemind/utils/formatting';
import { PortfolioViewType, ChartData } from '../types';
import './stats-ui.scss';

export interface StatsUIProps {
  // Data props
  totalAssets: number;
  totalPortfolioValue: number;
  totalBuckets: number;
  totalPnL: number;
  totalPnLPercentage: number;
  bucketNames: string[];
  assetChartData: ChartData[];
  bucketChartData: ChartData[];
  selectedBucketChartData: ChartData[];
  
  // State props
  viewType: PortfolioViewType;
  pnlDisplayMode: 'dollar' | 'percentage';
  selectedBucket: string;
  
  // Callback props
  onViewTypeChange: (viewType: PortfolioViewType) => void;
  onPnlDisplayModeToggle: () => void;
  onSelectedBucketChange: (bucket: string) => void;
}

export const StatsUI: React.FC<StatsUIProps> = ({
  totalAssets,
  totalPortfolioValue,
  totalBuckets,
  totalPnL,
  totalPnLPercentage,
  bucketNames,
  assetChartData,
  bucketChartData,
  selectedBucketChartData,
  viewType,
  pnlDisplayMode,
  selectedBucket,
  onViewTypeChange,
  onPnlDisplayModeToggle,
  onSelectedBucketChange,
}) => {
  const getPnLDisplayValue = (amount: number, percentage: number): string => {
    if (pnlDisplayMode === 'dollar') {
      return formatPnL(amount);
    } else {
      return formatPercentage(percentage);
    }
  };

  const renderVisualization = () => {
    if (totalAssets === 0) {
      return (
        <div className="stats__placeholder">
          <p>No assets in your portfolio yet!</p>
          <p>Add some assets in the Edit Assets page to see your portfolio data here.</p>
        </div>
      );
    }

    if (viewType === 'overall') {
      return (
        <div className="stats__large-chart">
          <PieChart
            data={assetChartData}
            title="All Assets Allocation"
            height={400}
            legend={{ pos: 'right' }}
          />
        </div>
      );
    } else if (viewType === 'bucket') {
      const bucketOptions = bucketNames.map(name => ({
        value: name,
        label: name,
      }));
      
      return (
        <div className="stats__bucket-view">
          <div className="stats__bucket-selector">
            <Select
              value={selectedBucket}
              onChange={(value) => onSelectedBucketChange(value as string)}
              options={bucketOptions}
            />
          </div>
          {selectedBucketChartData.length > 0 && (
            <PieChart
              data={selectedBucketChartData}
              height={400}
              legend={{ pos: 'right' }}
            />
          )}
        </div>
      );
    } else {
      return (
        <PieChart
          data={bucketChartData}
          title="Buckets Overview"
          height={400}
          legend={{ pos: 'right' }}
        />
      );
    }
  };

  return (
    <div className="stats">
      <div className="stats__header">
        <h2>Portfolio Statistics</h2>
      </div>

      <div className="stats__stats">
        <div className="stats__stat-card">
          <div className="stats__stat-value">{totalAssets}</div>
          <div className="stats__stat-label">Total Assets</div>
        </div>
        <div className="stats__stat-card">
          <div className="stats__stat-value">{formatCurrency(totalPortfolioValue)}</div>
          <div className="stats__stat-label">Portfolio Value</div>
        </div>
        <div className="stats__stat-card">
          <div className="stats__stat-value">{totalBuckets}</div>
          <div className="stats__stat-label">Buckets</div>
        </div>
        <div className="stats__stat-card stats__stat-card--pnl">
          <Button
            color="secondary"
            onClick={onPnlDisplayModeToggle}
            className="stats__pnl-toggle"
          >
            {pnlDisplayMode === 'dollar' ? '$' : '%'}
          </Button>
          <div className={`stats__stat-value ${totalPnL >= 0 ? 'stats__stat-value--positive' : 'stats__stat-value--negative'}`}>
            {getPnLDisplayValue(totalPnL, totalPnLPercentage)}
          </div>
          <div className="stats__stat-label">Total P&L</div>
        </div>
      </div>

      <div className="stats__card">
        <div className="stats__header">
          <h2>Portfolio Visualization</h2>
          {totalAssets > 0 && (
            <div className="stats__view-toggle">
              <Button
                color={viewType === 'overall' ? 'primary' : 'secondary'}
                onClick={() => onViewTypeChange('overall')}
              >
                All Assets
              </Button>
              <Button
                color={viewType === 'bucket' ? 'primary' : 'secondary'}
                onClick={() => onViewTypeChange('bucket')}
              >
                By Bucket
              </Button>
              <Button
                color={viewType === 'buckets' ? 'primary' : 'secondary'}
                onClick={() => onViewTypeChange('buckets')}
              >
                Buckets Overview
              </Button>
            </div>
          )}
        </div>
        
        <div className="stats__content">
          {renderVisualization()}
        </div>
      </div>
    </div>
  );
};
